import { useEffect, useRef, useState } from "react";
import { resolveMedia } from "@/lib/api";

/**
 * Hero video loop - rotates through small camera-detail clips behind the headline.
 * Two stacked video layers cross-fade so there is no poster flash between clips:
 * the next clip preloads invisibly, then fades in over the ending one.
 */
const CLIPS = [
  "/api/files/highwayslens/hero/d75fde7a-85ed-406b-a237-6689c2b2da0c.mp4",
  "/api/files/highwayslens/hero/940d8606-5662-4f77-be7f-5328d64e44e8.mp4",
  "/api/files/highwayslens/hero/5dca9e41-3934-4b98-beff-51796941049f.mp4",
  "/api/files/highwayslens/hero/b6299ba6-532f-435e-a431-016368ebc19d.mp4",
];
const POSTER = "/api/files/highwayslens/hero/a68f8c6c-ffad-4749-8d0c-fa4adf54be69.jpg";
const FADE_MS = 700;

export default function HeroVideoLoop() {
  const [reducedMotion, setReducedMotion] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  });
  const [front, setFront] = useState(0);
  const [layerSrc, setLayerSrc] = useState([CLIPS[0], CLIPS[1 % CLIPS.length]]);
  const nextClip = useRef(2 % CLIPS.length);
  const vA = useRef(null);
  const vB = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    vA.current?.play?.().catch(() => {});
  }, []);

  const handleEnded = (layer) => {
    if (layer !== front) return;
    const back = 1 - layer;
    const backVideo = back === 0 ? vA.current : vB.current;
    if (backVideo) {
      try { backVideo.currentTime = 0; } catch { /* not loaded yet */ }
      backVideo.play().catch(() => {});
    }
    setFront(back);
    // once the fade has finished, load the following clip into the hidden layer
    setTimeout(() => {
      setLayerSrc((prev) => {
        const copy = [...prev];
        copy[layer] = CLIPS[nextClip.current];
        return copy;
      });
      nextClip.current = (nextClip.current + 1) % CLIPS.length;
    }, FADE_MS + 150);
  };

  if (reducedMotion) {
    return (
      <div className="absolute inset-0 -z-10 overflow-hidden" aria-hidden="true">
        <img
          src={resolveMedia(POSTER)}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 -z-10 overflow-hidden" aria-hidden="true" data-testid="hero-loop">
      <img
        src={resolveMedia(POSTER)}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      {[0, 1].map((layer) => (
        <video
          key={layer}
          ref={layer === 0 ? vA : vB}
          src={resolveMedia(layerSrc[layer])}
          muted
          playsInline
          preload="auto"
          onEnded={() => handleEnded(layer)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out ${front === layer ? "opacity-100" : "opacity-0"}`}
          style={{ transitionDuration: `${FADE_MS}ms` }}
          data-testid={`hero-video-layer-${layer}`}
        />
      ))}
      <div className="absolute inset-0 hero-overlay" />
    </div>
  );
}
